import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Palette, Trash2 } from "lucide-react";

export default function PtoTypesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/pto/types");
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/tenant/pto/types/${id}`);
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (t, field, value) => {
    try {
      await api.patch(`/tenant/pto/types/${t.id}`, { [field]: value });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="pto-types-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Time admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">PTO types</h1>
          <p className="mt-2 text-muted-foreground">Vacation, Sick, Holiday, Personal — whatever your team tracks.</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="pto-type-new-btn"><Plus className="h-4 w-4" /> New type</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No PTO types yet. Create one above.</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((t) => (
            <Card key={t.id} data-testid={`pto-type-${t.code}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center gap-3">
                  <span className="inline-block h-8 w-8 rounded-lg" style={{ backgroundColor: t.color }} />
                  <div>
                    <CardTitle className="text-lg">{t.label}</CardTitle>
                    <div className="text-xs uppercase text-muted-foreground tracking-widest">{t.code}</div>
                  </div>
                </div>
                <Badge variant={t.active ? "default" : "secondary"}>{t.active ? "Active" : "Archived"}</Badge>
              </CardHeader>
              <CardContent className="flex items-center justify-between gap-3 text-sm">
                <label className="flex items-center gap-2">
                  <Switch checked={t.paid} onCheckedChange={(v) => toggle(t, "paid", v)} data-testid={`pto-type-paid-${t.code}`} />
                  <span className="text-muted-foreground">Paid</span>
                </label>
                <Button variant="ghost" size="sm" onClick={() => remove(t.id)} data-testid={`pto-type-delete-${t.code}`}><Trash2 className="h-4 w-4" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NewTypeDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
    </div>
  );
}

function NewTypeDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ code: "", label: "", paid: true, color: "#1f6feb" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ code: "", label: "", paid: true, color: "#1f6feb" }); }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tenant/pto/types", form);
      toast.success("Created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pto-type-new-dialog">
        <DialogHeader><DialogTitle>New PTO type</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="VAC" data-testid="pto-type-code" /></div>
            <div className="space-y-2"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Vacation" data-testid="pto-type-label" /></div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="flex items-center gap-2"><Palette className="h-4 w-4" /> Colour</Label>
            <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="pto-type-color" />
            <label className="ml-auto flex items-center gap-2">
              <Switch checked={form.paid} onCheckedChange={(v) => setForm({ ...form, paid: v })} data-testid="pto-type-paid-toggle" />
              <span className="text-sm text-muted-foreground">Paid</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.code || !form.label} data-testid="pto-type-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
